import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Flame } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import apiClient from '@/services/apiClient';

const PressingHistory = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search.trim()) params.append('search', search.trim());
      const res = await apiClient.get(`/pressing/history?${params}`);
      setSessions(res.data.sessions || []);
      setTotal(res.data.total || 0);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHistory(); }, [page]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pressing-history-page">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse/pressing')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-lg font-bold text-gray-900">Pressing History</h1>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-4">
        <div className="flex gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchHistory()}
            placeholder="Search by session ID or staff name..."
            data-testid="search-input"
          />
          <Button onClick={fetchHistory} data-testid="search-btn">
            <Search className="w-4 h-4" />
          </Button>
        </div>

        <Card>
          <CardContent className="pt-4">
            {loading ? (
              <p className="text-center text-gray-400 py-8">Loading...</p>
            ) : sessions.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No pressing sessions found</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Session ID</TableHead>
                      <TableHead>Staff</TableHead>
                      <TableHead className="text-center">USNs Pressed</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Completed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((s) => (
                      <TableRow key={s.session_id}>
                        <TableCell className="font-mono text-xs">{s.session_id}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <span className="font-medium">{s.staff_name || '-'}</span>
                            <span className="text-xs text-gray-400 ml-1">({s.staff_id})</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-bold">{s.total_scanned}</TableCell>
                        <TableCell>
                          <Badge className={
                            s.status === 'COMPLETED' ? 'bg-green-100 text-green-800 border-green-200' :
                            'bg-amber-100 text-amber-800 border-amber-200'
                          }>
                            {s.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {s.started_at ? new Date(s.started_at).toLocaleString() : '-'}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {s.completed_at ? new Date(s.completed_at).toLocaleString() : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {total > 20 && (
              <div className="flex justify-between items-center mt-4 pt-3 border-t">
                <span className="text-sm text-gray-500">Page {page + 1} of {Math.ceil(total / 20)}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>Prev</Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * 20 >= total}>Next</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PressingHistory;
